import { Box, Button, Fab, Grid2 as Grid, Typography } from '@mui/material';
import { ProductType } from 'types/Products';
import StarIcon from '@mui/icons-material/Star';
import StarIconOutline from '@mui/icons-material/StarOutline';
import { tss } from 'tss-react/mui';

import { TopContentProps } from './Types';
import { PictureCarousel } from '../../../../../common/PictureCarousel';
import { RequestsUXB } from './Requests/RequestsUXB';

import { useProductBookmarkModel } from 'components/pages/product/ProductPageModel';
import { useAuth } from 'react-oidc-context';
import { DeleteBookmark, PutBookmark } from 'api/services/BookmarkService';
import piwik from 'piwik/piwik';
import { toast } from 'react-toastify';
import ToolTipContent from 'components/common/ToolTipContent';
import { WHITELABEL_EVENT_KEY } from 'components/constants/Constants';
import { useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { useDownloadPublicFile } from 'helpers/utilsTS';
import { Asset } from 'types/Asset';
import { AssetTypes } from 'api/types/ReferenceData';
import {
  useIfVerifiedUser,
  useIsSupplierPortalProductEditorFieldDisabled
} from 'hooks/useUserState_utils';
import { t } from 'i18next';
import { LogEventType } from 'types/LogEvents';
import { useLogEvent } from 'api/services/LogEventService';

export interface ImgInterface {
  alt: string;
  src: string;
}

export const TopSection = ({
  imgs,
  content
}: {
  imgs: ImgInterface[];
  content: TopContentProps;
}) => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.topSection}
      sx={{ display: 'flex', gap: { xs: 2, md: 6 } }}
    >
      <div>
        <PictureCarousel arrowsPosition="Bottom" displayDots>
          {imgs.map((img, index) => (
            <img key={index} src={img.src} alt={img.alt} />
          ))}
        </PictureCarousel>
      </div>

      <TopContent content={content} />
    </Box>
  );
};
const useStyles = tss.create(({ theme }) => ({
  topSection: {
    display: 'grid',
    gridTemplateColumns: 'minmax(100px, 400px) 1fr',
    [`${theme.breakpoints.down('md')}`]: {
      gridTemplateColumns: '1fr'
    },
    '.container': {
      padding: 0
    }
  }
}));

const TopContent = ({ content }: { content: TopContentProps }) => {
  const { product } = content;
  const { isAuthenticated } = useAuth();
  const { event: eventKeyFromUrl } = useParams();
  const eventKey = WHITELABEL_EVENT_KEY ?? eventKeyFromUrl;
  const { bookmarked, setBookmarked } = useProductBookmarkModel(product);
  const navigate = useNavigate();
  const logEvent = useLogEvent();

  const onBookmarkClick = () => {
    if (isAuthenticated) {
      if (bookmarked) {
        DeleteBookmark(
          product,
          () => setBookmarked(false),
          () => {
            toast.error('Could not remove bookmark!');
          }
        );
      } else {
        PutBookmark(
          product,
          () => {
            setBookmarked(true);
            if (product.type === ProductType.INGREDIENTS && eventKey) {
              piwik.push([
                'trackEvent',
                'Bookmarks',
                'add',
                eventKey,
                product.name
              ]);
              logEvent(
                [
                  {
                    productId: product.old_key,
                    segment: product.segment,
                    supplierId: product.company.key
                  }
                ],
                product.type === ProductType.INGREDIENTS
                  ? LogEventType.BOOKMARK_ADDED
                  : product.type === ProductType.FORMULATIONS
                  ? LogEventType.FORMULATION_BOOKMARK_ADDED
                  : LogEventType.SERVICE_BOOKMARK_ADDED,
                eventKey
              );
            }
          },
          () => {
            toast.error('Could not add bookmark!');
          }
        );
      }
    }
  };

  const hideEditButton = useIsSupplierPortalProductEditorFieldDisabled();

  const userVerified = useIfVerifiedUser();
  const downloadPublicFile = useDownloadPublicFile();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 6,
        flexDirection: 'column'
      }}
    >
      {isAuthenticated && !hideEditButton && (
        <Fab
          sx={theme => ({
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: theme.spacing(2)
          })}
          color="primary"
          size="medium"
          variant="extended"
          onClick={() =>
            navigate(
              `/admin/product/${product.type}/givaudan/${product.segment}/${product.event.key}`
            )
          }
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit Product
        </Fab>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant={'h2'}>{content.title}</Typography>
          <ToolTipContent
            text={t(
              `search.uxbeauty.favorite.tooltips.${
                !isAuthenticated ? 'disabled' : bookmarked ? 'remove' : 'add'
              }`
            )}
            tooltip={true}
          >
            <div onClick={() => onBookmarkClick()}>
              {bookmarked ? (
                <StarIcon
                  sx={theme => ({
                    '&:hover': {
                      cursor: 'pointer'
                    },
                    color: theme.palette.primary.main
                  })}
                />
              ) : (
                <StarIconOutline
                  sx={theme => ({
                    color: isAuthenticated
                      ? theme.palette.primary.main
                      : theme.palette.primary.light,
                    '&:hover': {
                      cursor: isAuthenticated ? 'pointer' : 'not-allowed'
                    }
                  })}
                />
              )}
            </div>
          </ToolTipContent>
        </Box>
        <Typography variant={'h3'}>{content.tag}</Typography>
        <Typography variant={'body2'} sx={{ whiteSpace: 'pre-wrap' }}>
          {content.description}
        </Typography>
      </Box>
      <Box>
        {content?.assets && userVerified && (
          <>
            <Typography
              sx={{
                color: 'GrayText',
                marginBottom: 2
              }}
            >
              Download documents
            </Typography>
            <Grid
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              {content?.assets.map((asset: Asset) => {
                return (
                  <Button
                    variant="outlined"
                    key={asset.name}
                    disabled={!isAuthenticated}
                    onClick={() =>
                      downloadPublicFile(
                        asset,
                        [
                          {
                            productId: product.old_key,
                            segment: product.segment,
                            supplierId: product.company?.key
                          }
                        ],
                        eventKey
                      )
                    }
                  >
                    {AssetTypes.find(t => t.value === asset.type)?.label ??
                      asset.name}
                  </Button>
                );
              })}
            </Grid>
          </>
        )}
      </Box>
      <RequestsUXB product={product} />
    </Box>
  );
};
